import {
  Application,
  Container,
  Graphics,
  Ticker,
  FillGradient,
} from "pixi.js";

// --- Palette (provided brand colours) ----------------------------------
const COLOR_BG_TOP = 0x031e55; // deep blue (top of sky)
const COLOR_BG_MID = 0x07346d; // blue (middle)
const COLOR_BG_BOTTOM = 0x9b30b5; // purple (near the horizon)

const STAR_TINTS = [0xffffff, 0xf3ecff, 0xd9c6ff, 0xc6a6ff, 0xff66ff];
const SHOOTING_COLOR = 0xffffff;
const SHOOTING_TAIL = 0xff00ff; // magenta tail accent

// Per-category tail colours for the flying-star variety.
const KIND_TAIL: Record<string, number> = {
  streak: 0xff00ff, // magenta
  meteor: 0x66e0ff, // icy cyan
  drifter: 0xc6a6ff, // soft lavender
  comet: 0x9b30b5, // deep purple
  sparkler: 0xffd36e, // warm gold
  burst: 0xff66e0, // bright pink-magenta
};

interface Star {
  g: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  baseAlpha: number;
  twinkleSpeed: number;
  twinklePhase: number;
  twinkleDepth: number;
}

interface TrailPoint {
  x: number;
  y: number;
}

type ShootingKind =
  | "streak"
  | "meteor"
  | "drifter"
  | "comet"
  | "sparkler"
  | "burst";

interface ShootingStar {
  kind: ShootingKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  // curvature: slow turning of the velocity vector (radians / frame)
  curve: number;
  // wobble adds a gentle perpendicular sine drift for a non-rigid path
  wobbleAmp: number;
  wobbleFreq: number;
  wobblePhase: number;
  speed: number;
  trail: TrailPoint[];
  maxTrail: number;
  life: number;
  maxLife: number;
  width: number;
  // For "burst" stars: the life fraction (0–1) at which it explodes.
  burstAt: number | null;
  tailColor: number;
  headColor: number;
  glow: number; // head glow size multiplier
  sparkle: boolean; // emit little spark dots along the trail
}

// A glowing fragment thrown off when a "burst" star explodes.
interface Fragment {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
}

async function main(): Promise<void> {
  const canvas = document.getElementById("stage") as HTMLCanvasElement;

  const app = new Application();
  await app.init({
    canvas,
    resizeTo: window,
    antialias: true,
    backgroundAlpha: 0,
    resolution: Math.min(window.devicePixelRatio || 1, 3),
    autoDensity: true,
    powerPreference: "high-performance",
  });

  const stage = app.stage;
  stage.sortableChildren = true;

  const view = () => ({
    w: app.screen.width,
    h: app.screen.height,
  });

  // --- 1. Background sky gradient ---------------------------------------
  const background = new Graphics();
  background.zIndex = 0;
  stage.addChild(background);

  const drawBackground = () => {
    const { w, h } = view();
    background.clear();
    const gradient = new FillGradient(0, 0, 0, h);
    gradient.addColorStop(0, COLOR_BG_TOP);
    gradient.addColorStop(0.5, COLOR_BG_MID);
    gradient.addColorStop(1, COLOR_BG_BOTTOM);
    // Semi-transparent so the CSS body gradient shows through and the two
    // layer into a richer 3-colour space backdrop.
    background.rect(0, 0, w, h).fill({ fill: gradient, alpha: 0.25 });
  };

  // --- 2. Static twinkling starfield ------------------------------------
  const starLayer = new Container();
  starLayer.zIndex = 1;
  starLayer.alpha = 0.5;
  stage.addChild(starLayer);

  let stars: Star[] = [];

  const buildStars = () => {
    starLayer.removeChildren();
    stars = [];
    const { w, h } = view();

    // ~1 star per 4,200 px² — a sparser, calmer field.
    const count = Math.round((w * h) / 4200);

    for (let i = 0; i < count; i++) {
      const r = Math.random();
      // Continuous size distribution skewed toward tiny stars, with a rare
      // few bright "feature" stars — feels more natural than 3 fixed sizes.
      const radius =
        r > 0.985
          ? 1.7 + Math.random() * 0.8
          : r > 0.9
          ? 1.0 + Math.random() * 0.6
          : 0.35 + Math.random() * 0.55;
      const tint = STAR_TINTS[(Math.random() * STAR_TINTS.length) | 0]!;

      const g = new Graphics();
      // Layered soft bloom under the core (many rings = smooth falloff) so
      // bright stars glow naturally. Drawn at a slightly larger internal
      // radius then relying on resolution:3 + AA for crisp edges.
      if (radius >= 0.9) {
        g.circle(0, 0, radius * 5.5).fill({ color: tint, alpha: 0.035 });
        g.circle(0, 0, radius * 4.0).fill({ color: tint, alpha: 0.05 });
        g.circle(0, 0, radius * 2.8).fill({ color: tint, alpha: 0.08 });
        g.circle(0, 0, radius * 1.9).fill({ color: tint, alpha: 0.13 });
        g.circle(0, 0, radius * 1.3).fill({ color: tint, alpha: 0.22 });
      } else {
        // Faint glow even on tiny stars for a softer, higher-quality look.
        g.circle(0, 0, radius * 2.4).fill({ color: tint, alpha: 0.12 });
      }
      g.circle(0, 0, radius).fill({ color: tint });
      // Hot white core gives each star a crisp point of light.
      g.circle(0, 0, Math.max(0.35, radius * 0.5)).fill({ color: 0xffffff, alpha: 0.95 });

      // Rare bright stars get a soft 4-point diffraction flare (like a real
      // lens/eye sees bright stars) instead of a hard crosshair. Each spike
      // is a thin triangle that tapers to a point and blends additively.
      if (radius >= 1.6) {
        const flare = new Graphics();
        flare.blendMode = "add";
        const len = radius * (7 + Math.random() * 4);
        const halfW = Math.max(0.6, radius * 0.5);
        const drawSpike = (dx: number, dy: number, px: number, py: number) => {
          flare
            .poly([0, 0, dx * len, dy * len, px * halfW, py * halfW])
            .fill({ color: tint, alpha: 0.16 })
            .poly([0, 0, dx * len, dy * len, -px * halfW, -py * halfW])
            .fill({ color: tint, alpha: 0.16 });
        };
        // Horizontal + vertical spikes (perpendicular offset gives width).
        drawSpike(1, 0, 0, 1);
        drawSpike(-1, 0, 0, 1);
        drawSpike(0, 1, 1, 0);
        drawSpike(0, -1, 1, 0);
        // Faint short diagonal spikes for extra realism.
        const d = 0.7071;
        const dlen = len * 0.45;
        flare
          .poly([0, 0, d * dlen, d * dlen, -d * halfW, d * halfW])
          .fill({ color: tint, alpha: 0.08 })
          .poly([0, 0, -d * dlen, -d * dlen, d * halfW, -d * halfW])
          .fill({ color: tint, alpha: 0.08 })
          .poly([0, 0, d * dlen, -d * dlen, d * halfW, d * halfW])
          .fill({ color: tint, alpha: 0.08 })
          .poly([0, 0, -d * dlen, d * dlen, -d * halfW, -d * halfW])
          .fill({ color: tint, alpha: 0.08 });
        g.addChild(flare);
      }

      const x = Math.random() * w;
      const y = Math.random() * h;
      g.position.set(x, y);

      const baseAlpha = 0.3 + Math.random() * 0.7;
      g.alpha = baseAlpha;
      starLayer.addChild(g);

      // Slow, mostly-horizontal drift; bigger stars drift a touch faster so
      // the field gains a subtle parallax depth as it moves.
      const depth = Math.min(1, radius / 2);
      const driftSpeed = (0.05 + depth * 0.18) * (0.6 + Math.random() * 0.8);
      const driftAngle = Math.PI + (Math.random() - 0.5) * 0.6; // drift left-ish

      stars.push({
        g,
        x,
        y,
        vx: Math.cos(driftAngle) * driftSpeed,
        vy: Math.sin(driftAngle) * driftSpeed * 0.4,
        baseAlpha,
        twinkleSpeed: 0.3 + Math.random() * 2.0,
        twinklePhase: Math.random() * Math.PI * 2,
        // Larger stars twinkle less (they read as closer/steadier).
        twinkleDepth: radius >= 1.0 ? 0.2 + Math.random() * 0.25 : 0.4 + Math.random() * 0.45,
      });
    }
  };

  // --- 3. Shooting stars ------------------------------------------------
  const shootingLayer = new Container();
  shootingLayer.zIndex = 2;
  shootingLayer.alpha = 0.65;
  stage.addChild(shootingLayer);

  const shootingGfx = new Graphics();
  shootingGfx.blendMode = "add";
  shootingLayer.addChild(shootingGfx);

  const shootingStars: ShootingStar[] = [];
  const fragments: Fragment[] = [];

  const explode = (
    x: number,
    y: number,
    vx: number,
    vy: number,
    color: number,
  ) => {
    const n = 7 + ((Math.random() * 6) | 0);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.5;
      const sp = 1.5 + Math.random() * 3.5;
      fragments.push({
        x,
        y,
        // Inherit some forward momentum + radial scatter.
        vx: vx * 0.3 + Math.cos(a) * sp,
        vy: vy * 0.3 + Math.sin(a) * sp,
        life: 0,
        maxLife: 25 + Math.random() * 30,
        size: 0.8 + Math.random() * 1.4,
        color,
      });
    }
  };

  const spawnShootingStar = () => {
    const { w, h } = view();

    // Pick a category. Weights favour the classic streak; rarer kinds add
    // variety to the sky.
    const kr = Math.random();
    const kind: ShootingKind =
      kr < 0.36
        ? "streak"
        : kr < 0.58
        ? "meteor"
        : kr < 0.73
        ? "drifter"
        : kr < 0.85
        ? "comet"
        : kr < 0.93
        ? "sparkler"
        : "burst";

    // Per-category motion + look.
    let speed: number;
    let width: number;
    let maxTrail: number;
    let maxLife: number;
    let curveAmp: number;
    let wobbleAmp: number;
    let glow: number;
    let sparkle = false;
    let headColor = SHOOTING_COLOR;
    let burstAt: number | null = null;

    switch (kind) {
      case "meteor": // fast, thin, long icy streak
        speed = 14 + Math.random() * 8;
        width = 1.0 + Math.random() * 0.8;
        maxTrail = 24 + ((Math.random() * 12) | 0);
        maxLife = 70 + Math.random() * 50;
        curveAmp = 0.006;
        wobbleAmp = 0.02 + Math.random() * 0.05;
        glow = 1.4;
        break;
      case "drifter": // slow, fat, soft lavender glow
        speed = 4 + Math.random() * 3;
        width = 2.0 + Math.random() * 1.4;
        maxTrail = 14 + ((Math.random() * 8) | 0);
        maxLife = 160 + Math.random() * 90;
        curveAmp = 0.01;
        wobbleAmp = 0.08 + Math.random() * 0.12;
        glow = 2.2;
        break;
      case "comet": // medium, big bright head + purple tail
        speed = 8 + Math.random() * 5;
        width = 1.6 + Math.random() * 1.2;
        maxTrail = 28 + ((Math.random() * 14) | 0);
        maxLife = 120 + Math.random() * 70;
        curveAmp = 0.01;
        wobbleAmp = 0.04 + Math.random() * 0.08;
        glow = 3.0;
        headColor = 0xffe9ff;
        break;
      case "sparkler": // gold, emits little sparks along the path
        speed = 6 + Math.random() * 5;
        width = 1.2 + Math.random() * 1.0;
        maxTrail = 18 + ((Math.random() * 10) | 0);
        maxLife = 110 + Math.random() * 70;
        curveAmp = 0.014;
        wobbleAmp = 0.05 + Math.random() * 0.1;
        glow = 1.8;
        sparkle = true;
        headColor = 0xfff2c6;
        break;
      case "burst": // flies, then explodes into glowing fragments mid-air
        speed = 9 + Math.random() * 5;
        width = 1.4 + Math.random() * 1.0;
        maxTrail = 18 + ((Math.random() * 10) | 0);
        maxLife = 80 + Math.random() * 50;
        curveAmp = 0.01;
        wobbleAmp = 0.04 + Math.random() * 0.08;
        glow = 2.0;
        headColor = 0xffffff;
        // Explode somewhere in the middle of its flight.
        burstAt = 0.4 + Math.random() * 0.3;
        break;
      default: // "streak" — the classic magenta shooting star
        speed = 8 + Math.random() * 8;
        width = 1.1 + Math.random() * 1.3;
        maxTrail = 16 + ((Math.random() * 14) | 0);
        maxLife = 90 + Math.random() * 70;
        curveAmp = 0.012;
        wobbleAmp = 0.04 + Math.random() * 0.08;
        glow = 1.7;
        break;
    }

    // Spawn from a screen edge, biased toward the left/right sides so the
    // streaks fly in from the sides of the screen.
    const roll = Math.random();
    // 0 = left, 1 = right, 2 = top, 3 = bottom
    const edge = roll < 0.42 ? 0 : roll < 0.84 ? 1 : roll < 0.92 ? 2 : 3;
    const margin = 60;

    let x: number;
    let y: number;
    if (edge === 0) {
      x = -margin;
      y = Math.random() * h;
    } else if (edge === 1) {
      x = w + margin;
      y = Math.random() * h;
    } else if (edge === 2) {
      x = Math.random() * w;
      y = -margin;
    } else {
      x = Math.random() * w;
      y = h + margin;
    }

    // Aim toward a random interior target, then add a little spread.
    const tx = w * (0.2 + Math.random() * 0.6);
    const ty = h * (0.2 + Math.random() * 0.6);
    const angle = Math.atan2(ty - y, tx - x) + (Math.random() - 0.5) * 0.5;

    shootingStars.push({
      kind,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      curve: (Math.random() - 0.5) * curveAmp,
      wobbleAmp,
      wobbleFreq: 0.08 + Math.random() * 0.12,
      wobblePhase: Math.random() * Math.PI * 2,
      speed,
      trail: [{ x, y }],
      maxTrail,
      life: 0,
      maxLife,
      width,
      burstAt,
      tailColor: KIND_TAIL[kind]!,
      headColor,
      glow,
      sparkle,
    });
  };

  // --- 4. Resize --------------------------------------------------------
  const handleResize = () => {
    drawBackground();
    buildStars();
  };
  // Pixi fires this whenever resizeTo updates the canvas; covers the
  // initial layout and any window resize reliably.
  app.renderer.on("resize", handleResize);
  app.resize();
  handleResize();

  // Click anywhere triggers a new edge-spawned shooting star.
  window.addEventListener("pointerdown", () => spawnShootingStar());

  // --- 5. Main loop -----------------------------------------------------
  let shootTimer = 0;
  let nextShootGap = 40 + Math.random() * 80;
  let swayClock = 0;

  app.ticker.add((ticker: Ticker) => {
    const dt = ticker.deltaTime; // ~1 at 60fps
    const { w, h } = view();

    // Slow whole-field sway so the sky feels gently alive.
    swayClock += dt;
    starLayer.x = Math.sin(swayClock * 0.004) * 14;
    starLayer.y = Math.cos(swayClock * 0.0031) * 9;

    // Drift + twinkle each star.
    for (const s of stars) {
      s.x += s.vx * dt;
      s.y += s.vy * dt;

      // Wrap around edges (with a small margin) so the field is endless.
      if (s.x < -6) s.x = w + 6;
      else if (s.x > w + 6) s.x = -6;
      if (s.y < -6) s.y = h + 6;
      else if (s.y > h + 6) s.y = -6;
      s.g.position.set(s.x, s.y);

      s.twinklePhase += s.twinkleSpeed * dt * 0.04;
      const t = 0.5 + 0.5 * Math.sin(s.twinklePhase);
      s.g.alpha = s.baseAlpha * (1 - s.twinkleDepth + s.twinkleDepth * t);
    }

    // Shooting stars: integrate curved motion, keep a trail, redraw.
    shootingGfx.clear();
    for (let i = shootingStars.length - 1; i >= 0; i--) {
      const ss = shootingStars[i]!;
      ss.life += dt;

      // Turn the velocity vector slowly (curvature) for a bending path.
      const cos = Math.cos(ss.curve * dt);
      const sin = Math.sin(ss.curve * dt);
      const nvx = ss.vx * cos - ss.vy * sin;
      const nvy = ss.vx * sin + ss.vy * cos;
      ss.vx = nvx;
      ss.vy = nvy;

      // Perpendicular wobble so the streak isn't a rigid straight line.
      ss.wobblePhase += ss.wobbleFreq * dt;
      const sp = Math.hypot(ss.vx, ss.vy) || 1;
      const perpX = -ss.vy / sp;
      const perpY = ss.vx / sp;
      const wob = Math.cos(ss.wobblePhase) * ss.wobbleAmp;

      ss.x += (ss.vx + perpX * wob) * dt;
      ss.y += (ss.vy + perpY * wob) * dt;

      // Record trail history (the tail follows the real curved path).
      ss.trail.push({ x: ss.x, y: ss.y });
      if (ss.trail.length > ss.maxTrail) ss.trail.shift();

      const lifeT = ss.life / ss.maxLife;
      const fade =
        lifeT < 0.12 ? lifeT / 0.12 : 1 - Math.max(0, (lifeT - 0.6) / 0.4);
      const alpha = Math.max(0, Math.min(1, fade));

      // Burst stars explode into fragments once they reach burstAt, then
      // are removed (the fragments carry on).
      if (ss.burstAt !== null && lifeT >= ss.burstAt) {
        explode(ss.x, ss.y, ss.vx, ss.vy, ss.tailColor);
        // A brief bright flash at the burst point.
        shootingGfx
          .circle(ss.x, ss.y, ss.width * 5)
          .fill({ color: 0xffffff, alpha: 0.6 });
        shootingStars.splice(i, 1);
        continue;
      }

      // Draw the tail by connecting recorded points; older points are
      // dimmer and thinner, brightening toward the head.
      const pts = ss.trail;
      for (let p = 1; p < pts.length; p++) {
        const a0 = pts[p - 1]!;
        const a1 = pts[p]!;
        const t1 = p / (pts.length - 1 || 1);
        shootingGfx
          .moveTo(a0.x, a0.y)
          .lineTo(a1.x, a1.y)
          .stroke({
            color: t1 > 0.85 ? ss.headColor : ss.tailColor,
            width: Math.max(0.2, ss.width * t1),
            alpha: alpha * t1 * t1,
            cap: "round",
            join: "round",
          });
      }

      // Sparklers drop occasional tiny spark dots near the head.
      if (ss.sparkle && Math.random() < 0.6) {
        const sa = Math.random() * Math.PI * 2;
        const sr = ss.width * (1 + Math.random() * 3);
        shootingGfx
          .circle(ss.x + Math.cos(sa) * sr, ss.y + Math.sin(sa) * sr, 0.7)
          .fill({ color: ss.headColor, alpha: alpha * 0.9 });
      }

      // Bright head glow (size scales per category).
      shootingGfx
        .circle(ss.x, ss.y, ss.width * 1.7)
        .fill({ color: ss.headColor, alpha });
      shootingGfx
        .circle(ss.x, ss.y, ss.width * 2.6 * ss.glow)
        .fill({ color: ss.tailColor, alpha: alpha * 0.28 });

      const pad = 120;
      const offscreen =
        ss.x > w + pad ||
        ss.x < -pad ||
        ss.y > h + pad ||
        ss.y < -pad ||
        ss.life > ss.maxLife;
      if (offscreen) shootingStars.splice(i, 1);
    }

    // Fragments from exploded stars: scatter, slow down, fade, die.
    for (let i = fragments.length - 1; i >= 0; i--) {
      const f = fragments[i]!;
      f.life += dt;
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      // Gentle drag so the burst eases out.
      f.vx *= 0.97;
      f.vy *= 0.97;

      const fa = Math.max(0, 1 - f.life / f.maxLife);
      shootingGfx
        .circle(f.x, f.y, f.size)
        .fill({ color: 0xffffff, alpha: fa });
      shootingGfx
        .circle(f.x, f.y, f.size * 2.6)
        .fill({ color: f.color, alpha: fa * 0.35 });

      if (f.life > f.maxLife) fragments.splice(i, 1);
    }

    // Cadence: launch a new shooting star every so often.
    shootTimer += dt;
    if (shootTimer > nextShootGap) {
      shootTimer = 0;
      nextShootGap = 60 + Math.random() * 160; // ~1–4s between streaks
      spawnShootingStar();
    }
  });
}

main().catch((err) => {
  console.error("Failed to start Cosmo Stars:", err);
});
